<script lang="ts">
  type MascotState = 'idle' | 'wink' | 'sleep' | 'celebrate' | 'split' | 'surprised';
  /**
   * `primary` — the canonical gold→hot→violet V-tile, rotated -6°.
   * `twin`    — the cool cyan→primary→violet companion, rotated +6° so the
   *             two tiles "face" each other when placed side-by-side. Used
   *             by the Intro pair to express the "find your twin" brand
   *             language without redrawing the OG card here.
   * `purple`  — true violet ramp (lavender→indigo), tilted -3° so a trio
   *             with the A/D pair reads as a visual rhythm. Used by the
   *             Intro floater for the "P" lettermark.
   */
  type MascotVariant = 'primary' | 'twin' | 'purple';
  let {
    state = 'idle' as MascotState,
    size = 80,
    variant = 'primary' as MascotVariant,
    /** Glyph rendered on the tile face. Defaults to 'V' for the canonical
     *  VuTwin mark; pass a single character (e.g. 'A', 'D') to use the
     *  mascot as a lettermark. When a non-'V' glyph is set the eyes are
     *  hidden so the letter shape stays uncluttered. */
    letter = 'V'
  } = $props();

  // Reactive so prop changes flip the eyes off/on without manual refresh.
  // (svelte/valid-compile flags plain `const x = prop !== ...` because it
  // captures the first-render value only — runes-aware derivation fixes it.)
  const isLettermark = $derived(letter !== 'V');
</script>

<div
  class="mascot"
  class:variant-twin={variant === 'twin'}
  class:variant-purple={variant === 'purple'}
  class:state-celebrate={state === 'celebrate'}
  class:state-surprised={state === 'surprised'}
  class:state-split={state === 'split'}
  style:--s="{size}px"
>
  {#if state === 'split'}
    <div class="mascot-body twin twin-l">{letter}</div>
    <div class="mascot-body twin twin-r">{letter}</div>
  {:else}
    <div class="mascot-body" class:lettermark={isLettermark}>
      {letter}
      {#if !isLettermark}
        <div class="mascot-eyes">
          <div
            class="eye"
            class:wink={state === 'wink'}
            class:sleep={state === 'sleep'}
            class:wide={state === 'surprised'}
          ></div>
          <div
            class="eye"
            class:sleep={state === 'sleep'}
            class:wide={state === 'surprised'}
          ></div>
        </div>
      {/if}
    </div>
  {/if}
  {#if state === 'celebrate' || state === 'idle' || state === 'wink'}
    <span class="sparkle s1">✦</span>
    <span class="sparkle s2">✦</span>
    <span class="sparkle s3">✧</span>
  {/if}
</div>

<style>
  .mascot {
    width: var(--s);
    height: var(--s);
    position: relative;
    display: inline-block;
    flex-shrink: 0;
  }

  .mascot-body {
    width: 100%;
    height: 100%;
    border-radius: 24%;
    background: linear-gradient(135deg, var(--gold) 0%, var(--hot) 50%, var(--violet) 100%);
    display: grid;
    place-items: center;
    color: #000;
    font-weight: 900;
    font-size: calc(var(--s) * 0.55);
    line-height: 1;
    text-rendering: geometricPrecision;
    -webkit-font-smoothing: antialiased;
    transform: rotate(-6deg);
    box-shadow:
      0 0 0 4px var(--bg-deep),
      0 0 0 8px rgba(255, 216, 74, 0.4),
      0 0 50px rgba(255, 56, 188, 0.5),
      0 calc(var(--s) * 0.08) 0 0 var(--hot-deep);
    position: relative;
    overflow: hidden;
    animation: mascotBob 3s ease-in-out infinite;
  }

  /* Lettermark mode (eyes hidden): give the glyph a touch more weight so
     it occupies the face the way the V+eyes composition does, and pull
     it visually toward the optical centre of the rotated tile. */
  .mascot-body.lettermark {
    font-size: calc(var(--s) * 0.7);
    letter-spacing: -0.02em;
  }

  /* Glossy highlight on the face. */
  .mascot-body::before {
    content: '';
    position: absolute;
    top: 8%;
    left: 12%;
    width: 30%;
    height: 25%;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    filter: blur(2px);
    pointer-events: none;
  }

  .mascot-eyes {
    position: absolute;
    top: 22%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: calc(var(--s) * 0.1);
  }

  .eye {
    width: calc(var(--s) * 0.11);
    height: calc(var(--s) * 0.11);
    background: #000;
    border-radius: 50%;
    position: relative;
    transition: transform 180ms var(--spring-bouncy);
  }
  .eye::after {
    content: '';
    position: absolute;
    top: 18%;
    left: 22%;
    width: 30%;
    height: 30%;
    background: #fff;
    border-radius: 50%;
  }
  .eye.wink {
    transform: scaleY(0.15);
  }
  .eye.sleep {
    transform: scaleY(0.15);
  }
  .eye.wide {
    transform: scale(1.4);
  }

  .sparkle {
    position: absolute;
    color: var(--gold);
    text-shadow: 0 0 8px var(--gold);
    pointer-events: none;
    animation: sparkleFloat 2.4s ease-in-out infinite;
  }
  .s1 {
    top: -6%;
    right: 6%;
    font-size: calc(var(--s) * 0.22);
    transform: rotate(20deg);
  }
  .s2 {
    bottom: 4%;
    left: -6%;
    font-size: calc(var(--s) * 0.18);
    transform: rotate(-15deg);
    animation-delay: 0.6s;
  }
  .s3 {
    top: 35%;
    right: -4%;
    font-size: calc(var(--s) * 0.14);
    animation-delay: 1.2s;
  }

  /* Celebrate: a big bouncy rotate. */
  .state-celebrate .mascot-body {
    animation: mascotCelebrate 600ms var(--spring-bouncy);
  }

  /* Surprised: small "gasp" pop, eyes go wide. */
  .state-surprised .mascot-body {
    animation: mascotSurprised 380ms var(--spring-bouncy);
  }

  /* Split: two faces side by side overlap. */
  .state-split {
    position: relative;
  }
  .twin {
    position: absolute;
    width: 75%;
    height: 75%;
    top: 12%;
    font-size: calc(var(--s) * 0.4);
    box-shadow:
      0 0 0 3px var(--bg-deep),
      0 0 30px rgba(255, 56, 188, 0.5),
      0 calc(var(--s) * 0.05) 0 0 var(--hot-deep);
  }
  .twin-l {
    left: -2%;
    transform: rotate(-12deg);
  }
  .twin-r {
    right: -2%;
    transform: rotate(8deg);
    background: linear-gradient(135deg, var(--cyan) 0%, var(--hot) 50%, var(--violet) 100%);
  }

  /* ============ Keyframes ============ */
  @keyframes mascotBob {
    0%,
    100% {
      transform: rotate(-6deg) translateY(0);
    }
    50% {
      transform: rotate(-4deg) translateY(-3px);
    }
  }
  @keyframes mascotCelebrate {
    0% {
      transform: rotate(-6deg) scale(1);
    }
    40% {
      transform: rotate(8deg) scale(1.15);
    }
    70% {
      transform: rotate(-3deg) scale(0.95);
    }
    100% {
      transform: rotate(-6deg) scale(1);
    }
  }
  @keyframes mascotSurprised {
    0% {
      transform: rotate(-6deg) scale(1);
    }
    40% {
      transform: rotate(-10deg) scale(1.1);
    }
    100% {
      transform: rotate(-6deg) scale(1);
    }
  }
  @keyframes sparkleFloat {
    0%,
    100% {
      opacity: 0.4;
      transform: scale(0.9);
    }
    50% {
      opacity: 1;
      transform: scale(1.15);
    }
  }

  /* Reduced motion: keep the face visible, kill the bob + sparkles. */
  @media (prefers-reduced-motion: reduce) {
    .mascot-body,
    .sparkle {
      animation: none !important;
    }
  }

  /* ============ Twin variant — cool blue companion ============
     Mirrored rotation (+6° vs the primary's -6°) so a pair reads as
     "facing each other". A true blue gradient (sky → cyan → royal →
     indigo) keeps it visually distinct from the warm primary tile
     while still rhyming with the cyan accents used elsewhere in the
     app (Echo mode label, solo CTA). The double ring + outer glow
     swap to cyan/indigo so the bloom matches the palette without
     losing the crispness of the original mark. */
  .variant-twin .mascot-body {
    background: linear-gradient(
      135deg,
      #7feeff 0%,
      #00d4ff 35%,
      #3b82f6 70%,
      #4f46e5 100%
    );
    transform: rotate(6deg);
    box-shadow:
      0 0 0 4px var(--bg-deep),
      0 0 0 8px rgba(0, 212, 255, 0.4),
      0 0 50px rgba(59, 130, 246, 0.5),
      0 calc(var(--s) * 0.08) 0 0 #1e3a8a;
    animation: mascotBobTwin 3s ease-in-out infinite;
  }
  .variant-twin .sparkle {
    color: #7feeff;
    text-shadow: 0 0 8px #00d4ff;
  }
  /* Mirror the gloss to the opposite shoulder so the two tiles look
     lit by the same source and visually rhyme with each other. */
  .variant-twin .mascot-body::before {
    left: auto;
    right: 12%;
  }
  /* Mirror sparkle anchoring so the pair reads as a true mirror image. */
  .variant-twin .s1 {
    right: auto;
    left: 6%;
    transform: rotate(-20deg);
  }
  .variant-twin .s2 {
    left: auto;
    right: -6%;
    transform: rotate(15deg);
  }
  .variant-twin .s3 {
    right: auto;
    left: -4%;
  }
  /* Slight phase offset on the bob keeps the pair from looking like a
     single rigid graphic. */
  @keyframes mascotBobTwin {
    0%,
    100% {
      transform: rotate(6deg) translateY(0);
    }
    50% {
      transform: rotate(4deg) translateY(-3px);
    }
  }
  .variant-twin.state-celebrate .mascot-body {
    animation: mascotCelebrateTwin 600ms var(--spring-bouncy);
  }
  @keyframes mascotCelebrateTwin {
    0% {
      transform: rotate(6deg) scale(1);
    }
    40% {
      transform: rotate(-8deg) scale(1.15);
    }
    70% {
      transform: rotate(3deg) scale(0.95);
    }
    100% {
      transform: rotate(6deg) scale(1);
    }
  }
  .variant-twin.state-surprised .mascot-body {
    animation: mascotSurprisedTwin 380ms var(--spring-bouncy);
  }
  @keyframes mascotSurprisedTwin {
    0% {
      transform: rotate(6deg) scale(1);
    }
    40% {
      transform: rotate(10deg) scale(1.1);
    }
    100% {
      transform: rotate(6deg) scale(1);
    }
  }
  :global(body.brutal) .variant-twin .mascot-body {
    transform: rotate(6deg);
  }

  /* ============ Purple variant — violet lettermark ============
     A true violet ramp (lavender → soft purple → royal → indigo) that
     sits between the warm primary and the cool twin in colour space, so
     a future trio reads as gold→purple→cyan, a left-to-right warm-to-
     cool gradient. Tilted -3° (subtler than the ±6° of the A/D pair) so
     the three tiles together form a small visual rhythm rather than
     three identical-leaning shapes. */
  .variant-purple .mascot-body {
    background: linear-gradient(
      135deg,
      #c4b5fd 0%,
      #a78bfa 35%,
      #8b5cf6 70%,
      #6d28d9 100%
    );
    transform: rotate(-3deg);
    box-shadow:
      0 0 0 4px var(--bg-deep),
      0 0 0 8px rgba(167, 139, 250, 0.4),
      0 0 50px rgba(139, 92, 246, 0.5),
      0 calc(var(--s) * 0.08) 0 0 #4c1d95;
    animation: mascotBobPurple 3s ease-in-out infinite;
  }
  .variant-purple .sparkle {
    color: #c4b5fd;
    text-shadow: 0 0 8px #a78bfa;
  }
  /* Phase-offset bob: same period & amplitude as the gold/blue tiles so
     a trio bobs in loose phase rather than lockstep. */
  @keyframes mascotBobPurple {
    0%,
    100% {
      transform: rotate(-3deg) translateY(0);
    }
    50% {
      transform: rotate(-1deg) translateY(-3px);
    }
  }
  :global(body.brutal) .variant-purple .mascot-body {
    transform: rotate(-3deg);
  }

  /* Brutalist override: collapse to monochrome plate with hard offset. */
  :global(body.brutal) .mascot-body {
    background: var(--text);
    color: var(--bg);
    box-shadow:
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      4px 4px 0 0 var(--text);
    transform: rotate(-6deg);
    animation: none;
  }
  :global(body.brutal) .sparkle {
    display: none;
  }
</style>
